import { useCallback, useMemo } from "react";
import { useGetDashboardQuery } from "@webcore/services/api/api";
import { FullViewSpinner } from "@webcore/components/core/FullViewSpinner/FullViewSpinner";
import { TeamRole, KpiDataResponse } from "@webcore/models/coreModels";
import CustomCodeRunner from "@webcore/components/domain/dashboard/renderers/custom/CustomCodeRunner";
import { useOpsAppPlatform } from "@webcore/context-providers/OpsAppPlatformProvider";
import { useDataChangeVersion } from "@webcore/hooks/useDataChangeVersion";
import { getClientInstanceId } from "@webcore/services/clientInstanceId";

interface CustomViewRendererProps {
  dashboardId: string;
  viewId: string;
  dataPipelineConfigurationId: string;
}

/**
 * Shared renderer for a custom dashboard view's code. In the MFE model it runs inside the ops
 * container under the read-only MFE session. All requests go through the same-origin /api/v1 path
 * so the read-only cookie is sent. The host renders under an OpsAppPlatformProvider, which supplies
 * the platform (user context, objects and documents) injected into the view's scope.
 */
export default function CustomViewRenderer({ dashboardId, viewId, dataPipelineConfigurationId }: CustomViewRendererProps) {
  const { data: dashboard, isLoading, isError } = useGetDashboardQuery({ id: dashboardId });
  const { userContext, opsAppObjects, opsAppDocuments } = useOpsAppPlatform();

  // The view's KPI data is fetched by the view's own code through fetchKpiData, so it lives outside
  // the RTK Query cache and invalidating the KpiData tag cannot refresh it. Remounting the code
  // on each invalidation re-runs those fetches — this is what carries a completed gold pipeline run
  // (which lands as a TeamSyncStatus change → SignalR → KpiData) through to the rendered view.
  // Scoped to this view's configuration: only its own config's data going live remounts it.
  const kpiDataVersion = useDataChangeVersion("KpiData", dataPipelineConfigurationId);

  const viewCode = useMemo(() => {
    if (dashboard === undefined) {
      return null;
    }

    const view = dashboard.dashboardViews.find(v => v.id === viewId);
    return view?.viewCode ?? null;
  }, [dashboard, viewId]);

  const kpiDefinitions = useMemo(() => dashboard?.kpiDefinitions ?? [], [dashboard]);

  const fetchKpiData = useCallback(async (kpiId: string, filters?: Record<string, string>): Promise<KpiDataResponse> => {
    const params = new URLSearchParams(filters ?? {});
    const queryString = params.toString();
    const url = `/api/v1/teams/active/dashboards/${dashboardId}/kpis/${kpiId}/data/${encodeURIComponent(dataPipelineConfigurationId)}${queryString ? `?${queryString}` : ""}`;
    // The instance id keeps any write this request provokes attributable for echo suppression,
    // the same as every request that goes through the shared BaseQuery.
    const response = await fetch(url, {
      credentials: "include",
      headers: { "X-Client-Instance-Id": getClientInstanceId() },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch KPI data: ${response.status} ${response.statusText}`);
    }

    const envelope = await response.json();
    return envelope.data;
  }, [dashboardId, dataPipelineConfigurationId]);

  if (isLoading) {
    return <FullViewSpinner />;
  }

  if (isError) {
    return <div className="text-danger p-3">Failed to load dashboard.</div>;
  }

  if (viewCode === null) {
    return <div className="text-danger p-3">No custom view code found.</div>;
  }

  return (
    <div className="w-100 vh-100 overflow-auto">
      <CustomCodeRunner key={kpiDataVersion} code={viewCode} label="View Error" scope={{ kpiDefinitions, fetchKpiData, opsAppObjects, opsAppDocuments, TeamRole, userContext }} />
    </div>
  );
}
